import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/compat';
import CheckSimple from '../icons/CheckSimple';
import Cross from '../icons/Cross';
import { slugify } from '../lib/purify';

/**
 * Accordion.
 *
 * @param {boolean} props.active Force open.
 * @param {string} props.title Title of the accordion
 * @param {string} props.hTag HTML tag to use for the title
 * @param {h.JSX.Element} props.children Children of the accordion
 * @returns {h.JSX.Element}
 */
const Accordion = ({
	children,
	title,
	active = false,
	hTag = '3',
	valid = false,
}: {
	children: h.JSX.Element;
	title: string;
	active?: boolean;
	hTag?: '1'|'2'|'3'|'4'|'5'|'6';
	valid?: boolean;
}) => {
	const [expanded, setExpanded] = useState(false);
	const uuid = useMemo((): string => `acc-${slugify(title)}`, [title]);
	const HTag: keyof h.JSX.IntrinsicElements = `h${hTag}` as any;

	/**
	 * Allow forcing open only.
	 */
	useEffect(() => {
		setExpanded(active);
	}, [active]);

	return (
		<div className={[
			'ea-accordion',
			expanded ? 'active' : 'inactive',
		].join(' ')}
		>
			<div className="ea-accordion__top">
				<HTag>
					<button
						type="button"
						aria-expanded={expanded}
						className={[
							'ea-accordion__trigger',
							valid ? 'ea-accordion__trigger--valid' : '',
							expanded ? 'ea-accordion__trigger--active' : '',
						].join(' ')}
						aria-controls={uuid}
						id={`trigger-${uuid}`}
						onClick={() => setExpanded(!expanded)}
						data-valid={valid !== false}
					>
						<span className="ea-accordion__title h6">
							{title}
						</span>
						<span className="ea-accordion__icon">
							{expanded || valid ? (
								<CheckSimple />
							) : (
								<Cross />
							)}
						</span>
					</button>
				</HTag>
			</div>
			<div
				id={uuid}
				role="region"
				aria-labelledby={`trigger-${uuid}`}
				className="ea-accordion__panel"
				hidden={!expanded}
				aria-hidden={!expanded}
			>
				<div className="ea-accordion__panel-hider">
					<div>
						{children}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Accordion;
